const XLSX_MIME = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

async function readExportError(res) {
  const contentType = (res.headers.get('Content-Type') || '').toLowerCase();
  if (contentType.includes('application/json')) {
    try {
      return await res.json();
    } catch {
      return {};
    }
  }
  const text = await res.text();
  return { message: text };
}

function promptTwoFactorCode() {
  if (typeof window === 'undefined') return null;
  const code = window.prompt('Tu cuenta tiene 2FA activo. Ingresá el código de 6 dígitos de tu app autenticadora:');
  if (!code || !String(code).trim()) return null;
  return String(code).trim();
}

/**
 * Descarga export tabular del API (Excel por defecto).
 * Reintenta con 2FA si el servidor responde TWO_FACTOR_REQUIRED.
 */
export async function downloadTabularExport(secureFetch, apiPath, fallbackFilename, options = {}) {
  const sep = apiPath.includes('?') ? '&' : '?';
  const url = `${apiPath}${sep}format=xlsx`;
  const headers = {};
  if (options.twoFactorCode) {
    headers['X-Contsuite-2FA'] = options.twoFactorCode;
  }

  const res = await secureFetch(url, { headers });
  if (res.status === 403) {
    const data = await readExportError(res);
    if (data.code === 'TWO_FACTOR_REQUIRED' && !options.twoFactorCode) {
      const code = promptTwoFactorCode();
      if (!code) throw new Error('Descarga cancelada: se requiere código 2FA.');
      return downloadTabularExport(secureFetch, apiPath, fallbackFilename, { twoFactorCode: code });
    }
    throw new Error(data.message || 'No tenés permiso para exportar.');
  }

  if (!res.ok) {
    const data = await readExportError(res);
    throw new Error(data.message || 'Error al exportar');
  }

  const contentType = (res.headers.get('Content-Type') || '').toLowerCase();
  if (contentType.includes('text/csv') || contentType.includes('text/plain')) {
    throw new Error(
      'El servidor respondió CSV en lugar de Excel. Reiniciá el backend (cd backend && npm start) o desplegá la última versión.'
    );
  }
  if (!contentType.includes('spreadsheet') && !contentType.includes('octet-stream')) {
    console.warn('[Contsuite] Content-Type inesperado en export:', contentType);
  }
  const blob = await res.blob();
  if (blob.size < 2000 && contentType.includes('json')) {
    throw new Error('La exportación falló en el servidor.');
  }
  const disposition = res.headers.get('Content-Disposition') || '';
  const match = disposition.match(/filename="([^"]+)"/i);
  const filename = match?.[1] || fallbackFilename;
  const blobUrl = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = blobUrl;
  a.download = filename.endsWith('.xlsx') ? filename : `${filename}.xlsx`;
  a.click();
  URL.revokeObjectURL(blobUrl);
  return a.download;
}

/**
 * Descarga binaria (zip, pdf, csv) con soporte 2FA.
 */
export async function downloadBinaryExport(secureFetch, apiPath, fallbackFilename, options = {}) {
  const headers = {};
  if (options.twoFactorCode) {
    headers['X-Contsuite-2FA'] = options.twoFactorCode;
  }

  const res = await secureFetch(apiPath, { headers });
  if (res.status === 403) {
    const data = await readExportError(res);
    if (data.code === 'TWO_FACTOR_REQUIRED' && !options.twoFactorCode) {
      const code = promptTwoFactorCode();
      if (!code) throw new Error('Descarga cancelada: se requiere código 2FA.');
      return downloadBinaryExport(secureFetch, apiPath, fallbackFilename, { twoFactorCode: code });
    }
    throw new Error(data.message || 'No tenés permiso para exportar.');
  }

  if (!res.ok) {
    const data = await readExportError(res);
    throw new Error(data.message || 'Error al exportar');
  }

  const blob = await res.blob();
  const disposition = res.headers.get('Content-Disposition') || '';
  const match = disposition.match(/filename="([^"]+)"/i);
  const filename = match?.[1] || fallbackFilename;
  const blobUrl = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = blobUrl;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(blobUrl);
  return filename;
}

export { XLSX_MIME };
